// @flow
import React, {FunctionComponent} from 'react';
import FlexCard from "./flex-card";
import {Divider, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography} from "@mui/material";
import {Error} from '@mui/icons-material';
import RouteService from "../../services/route-service";
import {RequestRegistrationController$AnimalSimilarityJson} from "../../api/generated/registry-rest";
import {JsonAnimal} from "../../api/generated/rest-dto";
import {PanonIdentifier} from "../../api/generated/herd-animal";
import {useNav} from "../Common/hooks/use-nav";
import {useBulkAnimals} from "../../hooks/use-bulk-animals";
import {InfoBox} from "../Common/alerts";
import {LoadingFallback} from "../Common/loading-fallback";
import {AnimalService} from "../../services/animal-service";
import {AnimalAvatar} from "../Common/animal-avatar";

type ReviewSimilaritiesCardProps = {
    similarAnimals: RequestRegistrationController$AnimalSimilarityJson[];
    reviewedAnimal: JsonAnimal;
    reviewId: string;
};

const OverlappingPropertiesIndicator = ({matching}: {
    matching: RequestRegistrationController$AnimalSimilarityJson | undefined
}) => {
    if (!matching) {
        return null;
    }

    if (matching.score > 4) {
        return (<Error alignmentBaseline="baseline" fontSize="large" color="warning"/>);
    }

    return (
        <Typography variant="subtitle2" color="text.primary" sx={{display: 'inline'}} component="span">
            {matching.score}
        </Typography>
    );
};

const ReviewSimilaritiesCard: FunctionComponent<ReviewSimilaritiesCardProps> = ({reviewId, reviewedAnimal, similarAnimals}) => {
    const nav = useNav();

    const handleSimilarityCardClick = (reviewId: string, similarAnimal: JsonAnimal) => {
        nav.push(
            RouteService.REGISTRY_ANIMAL_REVIEW_COMPARE_SIMILARITIES,
            {
                reviewedAnimalPanonId: reviewedAnimal.panonIdentifier.id,
                similarAnimalPanonId: similarAnimal.panonIdentifier.id
            },
            {
                reviewedAnimal,
                similarAnimal
            });
    }

    const findMatchingSaByPanonId = (panonId: PanonIdentifier): RequestRegistrationController$AnimalSimilarityJson | undefined => similarAnimals.find(simAnimal => simAnimal.panonIdentifier === panonId.id);

    const {data: similarAnimalsDetails = new Map<string, JsonAnimal>(), isLoading} = useBulkAnimals(similarAnimals.map(sa => sa.panonIdentifier));

    //TODO avatarUrl is needed in JsonAnimal

    return (
        <FlexCard title="Ähnlich zu">
            {isLoading && <LoadingFallback message="Ähnliche Tiere werden gesucht"/>}
            {!similarAnimalsDetails.size && !isLoading && <InfoBox>Keine ähnlichen Tiere gefunden</InfoBox>}
            <List sx={{width: '100%', bgcolor: 'background.paper'}}>
                {Array.from(similarAnimalsDetails.values()).map(sa =>
                    <>
                        <ListItem alignItems="flex-start">
                            <ListItemButton onClick={() => handleSimilarityCardClick(reviewId, sa)}>
                                <ListItemAvatar>
                                    <AnimalAvatar animal={sa}/>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={AnimalService.getAnimalFullName(sa) + " - " + sa.dateOfBirth.toLocaleString()}
                                    secondary={
                                        <>
                                            <Typography
                                                sx={{display: 'inline'}}
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                Ähnlichkeiten:
                                                {' '}
                                            </Typography>
                                            <OverlappingPropertiesIndicator matching={findMatchingSaByPanonId(sa.panonIdentifier)}/>
                                        </>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                        <Divider variant="inset" component="li"/>
                    </>
                )}
            </List>
        </FlexCard>
    );
};

export default ReviewSimilaritiesCard
